import { JsonAncestorReport } from '@ltvco/refresh-lib/fmt';
import { Grid, Box } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  useScreenSize,
} from '@ltvco/refresh-lib/utils';
import {
  AncestorFacts,
  AncestorOverview,
  type Relative,
  ReportLoading,
} from '@ltvco/refresh-lib/v1';
import {
  AncestorRelationships,
  Memory,
  ReportGeneratingScreen,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  AncestorMemories,
  AncestorMemoryModal,
  ReportNullState,
  useAncestorReport,
} from '@ltvco/refresh-lib/v2';
import { useLocation } from 'react-router-dom';

import { ReportBannerContainer } from './SearchBanners/components';
import { ObituarySearchBanner } from './SearchBanners/ObituarySearchBanner';
import { PersonSearchBanner } from './SearchBanners/PersonSearchBanner';
import { ReportRouteProps } from './types';

interface AncestorReportProps extends ReportRouteProps {}

export const AncestorReportPage = ({
  permalink,
  firstReportScreen,
}: AncestorReportProps) => {
  const { isMobile } = useScreenSize();
  const {
    queryResult,
    person,
    personValues,
    selectedMemory,
    selectedMemoryText,
    isMemoryText,
    showPersonBanner,
    showObituaryBanner,
    ancestorNavLinkData,
    ancestorSearchParams,
    isCreatingFamilyTree,
    reportInFamilyTreeData,
    reportsData,
    bulkAddSubject,
    familyTreeData,
    reportPostIndi,
    triggerUpdate,
    onMemoryModalClose,
    onDocumentSelect,
    handleAddingRelativeToFamilyTree,
    setSelectedMemory,
  } = useAncestorReport(permalink);
  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();

  // Scroll to the section of the report when the user navigates to the report
  useScrollToSectionOnNavigate(hash, queryResult);

  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="ancestor"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (queryResult.isLoading || !bulkAddSubject || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }

  if (zeroed || !person) {
    return <ReportNullState />;
  }

  if (selectedMemory && isMobile) {
    return (
      <AncestorMemoryModal
        memory={selectedMemory}
        onClose={() => onMemoryModalClose()}
        isPureText={isMemoryText}
        text={selectedMemoryText}
      />
    );
  }

  return (
    <Box marginTop={0}>
      {selectedMemory && !isMobile && (
        <AncestorMemoryModal
          memory={selectedMemory}
          onClose={() => onMemoryModalClose()}
          isPureText={isMemoryText}
          text={selectedMemoryText}
        />
      )}
      <Grid container direction={'row'} columns={12} spacing={20}>
        <Grid
          size={{ sm: 12, md: 3, lg: 3 }}
          sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' } }}
        >
          <ReportNavigationMenu
            reportType="Ancestor Report"
            headerTitle={personValues.fullName || ''}
            menuItems={ancestorNavLinkData}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 9, lg: 9 }} marginTop={3} ml={0} width="100%">
          <AncestorOverview
            familySearchId={personValues.familySearchId || ''}
            permalink={permalink}
            fullName={personValues.fullName || ''}
            otherNames={personValues.otherNames || ''}
            dateOfBirth={personValues.dateOfBirth}
            birthAddress={personValues.birthAddress || ''}
            dateOfDeath={personValues.dateOfDeath}
            deathAddress={personValues.deathAddress || ''}
            dateOfBurial={personValues.dateOfBurial}
            burialAddress={personValues.burialAddress || ''}
            reportInFamilyTree={!!reportInFamilyTreeData}
            addRelativeToFamilyTree={handleAddingRelativeToFamilyTree}
            isCreatingFamilyTree={isCreatingFamilyTree}
            photos={personValues.photos}
          />
          <AncestorFacts
            facts={person.facts}
            memberInFamilyTreeData={
              reportInFamilyTreeData as unknown as JsonAncestorReport
            }
            permalink={permalink}
          />
          <AncestorRelationships
            relatives={personValues.relatives as unknown as Relative[]}
            ancestorReportsInTree={
              reportsData?.reports as JsonAncestorReport[] | undefined
            }
            bulkAddSubject={bulkAddSubject}
            hasFamilyTreeCreated={!!familyTreeData}
            reportIndi={reportPostIndi}
          />
          <AncestorMemories
            memories={personValues.memories as Memory[]}
            onMemorySelect={(memory: Memory) => setSelectedMemory(memory)}
            onDocumentSelect={(memory: Memory) => onDocumentSelect(memory)}
          />
          <ReportBannerContainer>
            {/*
            Obituary Report Banner Logic:
            - Show if person died after 2000
            - Show if person has no death date AND was born after 1900
          */}
            {showObituaryBanner && (
              <ObituarySearchBanner searchParams={ancestorSearchParams} />
            )}
            {/*
              Person Report Banner Logic:
              - Show if person died after 1960
              - Show if person has no death date AND was born after 1850
            */}
            {showPersonBanner && (
              <PersonSearchBanner searchParams={ancestorSearchParams} />
            )}
          </ReportBannerContainer>
        </Grid>
      </Grid>
      <ReportNavigationMobileMenu
        reportType="Ancestor Report"
        headerTitle={personValues.fullName || ''}
        menuItems={ancestorNavLinkData}
      />
    </Box>
  );
};
